import React, { useCallback } from 'react';
import fetch from 'isomorphic-unfetch';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import styles from './newsletter-form.module.scss';
import PhoneModelSelect from '../PhoneModelSelect/PhoneModelSelect';
import ReCAPTCHA from 'react-google-recaptcha';
import { site_captcha_key } from '../../constants';
import { CheckmarkIcon, useBreakpoint } from '@buzzfeed/react-components';
import { captureException } from '@sentry/nextjs';
import { isAndroidAgent } from '@buzzfeed/bf-utils';
import pixelLoader from '../../public/static/images/pixel-loader.gif';
import { BFIslandButton, BFIslandColors } from '@buzzfeed/react-components';
import { useTrackingContext } from '../../hooks';

const validEmailRegExpr =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
const isValidEmail = (email) => validEmailRegExpr.test(email);

const NewsletterFormError = ({ children }) => (
  <p id="newsletter-error" className={styles.error} role="alert">
    {children}
  </p>
);
function NewsletterForm({ onSubmitSuccess, listId = 'buzzfeed_email_island' }) {
  const recaptchaRef = useRef();
  const { trackContentAction } = useTrackingContext();
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneModel, setPhoneModel] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isAndroidUser, setIsAndroidUser] = useState(false);
  const isMobile = useBreakpoint('(max-width: 52rem)');

  useEffect(() => {
    setIsAndroidUser(isAndroidAgent());
  }, []);

  const validateForm = () => {
    if (!isValidEmail(emailAddress)) {
      return 'Please enter a valid email';
    }
    if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
      return 'Please enter a valid phone number';
    }
  };

  const handleFormSubmit = useCallback(
    async (event) => {
      setError(null);
      event.preventDefault();
      if (submitting) {
        return;
      }
      setSuccess(false);

      const validationError = validateForm();
      if (validationError) {
        setError(validationError);
        return;
      }

      const token = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();

      setSubmitting(true);
      const isAndroid = isMobile ? isAndroidUser : phoneModel === 'android';
      const subscriptions = isAndroid
        ? [`${listId}_android`]
        : [listId];
      const body = {
        brand: 'buzzfeed',
        subscriptions,
        source: 'buzzfeed-island',
        'g-recaptcha-response': token,
        email: emailAddress,
        phone_number: phoneNumber,
      };

      const response = await fetch('/newsletters/api/subhub/v1/users', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setSubmitting(false);

      if (!response.ok) {
        captureException(
          new Error(`Newsletter signup failed with status ${response.status}`)
        );
        return setError('Uh-oh! Something went wrong. Please try again.');
      }

      setError(null);
      setSuccess(true);
      trackContentAction({
        unit_type: 'component',
        unit_name: 'signup',
        item_type: 'button',
        item_name: 'submission',
        position_in_unit: 0,
        action_type: 'signup',
        action_value: listId,
      });
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      sessionStorage.setItem('newsletterAddressable', 'true');
      
    },
    [
      submitting,
      validateForm,
      isMobile,
      isAndroidUser,
      phoneModel,
      emailAddress,
      phoneNumber,
      onSubmitSuccess,
      recaptchaRef,
      listId,
      trackContentAction,
    ]
  );

  const handleEmailChange = (e) => {
    setEmailAddress(e.target.value);
    if (error && error.includes('email')) {
      setError(null);
    }
  };

  return (
    <form
      className={`${styles.form} ${isAndroidUser ? styles.androidForm : ''}`}
    >
      <ReCAPTCHA
        ref={recaptchaRef}
        size="invisible"
        sitekey={site_captcha_key}
        style={{ visibility: 'hidden' }}
      />
      {!success && (
        <fieldset className={`${styles.fieldset}`}>
          <legend className={styles.legend}>Island signup form</legend>
          <div className={styles.submitBox}>
            <div
              className={`${styles.inputsGroup} ${submitting ? styles.loading : ''}`}
            >
              <div
                className={`${styles.inputContainer} ${error?.includes('email') ? styles.invalid : ''}`}
              >
                <input
                  className={`${styles.input} ${styles.emailInput}`}
                  id="newsletter-email-signup"
                  type="email"
                  name="email"
                  value={emailAddress}
                  onChange={handleEmailChange}
                  autoComplete="email"
                  placeholder="Your email"
                  aria-label="Email Address"
                  aria-describedby={
                    error
                      ? 'newsletter-email-signup-error'
                      : success
                        ? 'newsletter-success'
                        : null
                  }
                  size={15}
                  required
                />
                <CheckmarkIcon className={styles.checkIcon} />
              </div>
              <div
                className={`${styles.inputContainer} ${error?.includes('phone') ? styles.invalid : ''}`}
              >
                <PhoneInput
                  placeholder="Your phone number"
                  defaultCountry="US"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  className={styles.input}
                />
                <CheckmarkIcon className={styles.checkIcon} />
              </div>
              <div className={styles.phoneModelContainer}>
                <PhoneModelSelect value={phoneModel} onChange={setPhoneModel} />
              </div>
            </div>
            {(() => {
              const emailValid = emailAddress !== '';
              const phoneValid = phoneNumber !== '';
              const phoneModelChosen = !!phoneModel;
              // Mobile: only email required. Desktop: email + phone model.
              const allValidDesktop =
                emailValid && phoneValid && phoneModelChosen;
              const allValidMobile = emailValid && phoneValid; // phone model optional on mobile
              const canActivate =
                (isMobile ? allValidMobile : allValidDesktop) && !success;
              const disabled = submitting || success || !canActivate;

              return (
                <BFIslandButton
                  className={styles.submitButton}
                  text="Claim your place"
                  size="large"
                  fullWidth
                  bgColor={BFIslandColors.CYAN}
                  disabled={disabled}
                  loading={submitting}
                  loader={<img src={pixelLoader} alt="Loading..." />}
                  onClick={handleFormSubmit}
                  type="submit"
                />
              );
            })()}
            {error && <NewsletterFormError>{error}</NewsletterFormError>}
          </div>
        </fieldset>
      )}
      {success && (
        <div className={styles.success}>
          <span>Success!</span>
          <span>We’ll be in touch.</span>
        </div>
      )}
    </form>
  );
}

NewsletterForm.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  onSubmitSuccess: PropTypes.func,
  listId: PropTypes.string,
};

export default NewsletterForm;
